import type {
  FeatureAnnouncementPageDefinition,
  MonthGroup,
} from '@/cmsBackend/transformers/types/featureAnnouncement';
import { useTheme } from '@/common/contexts/ContextProvider';
import { Env, Theme } from '@/common/types';
import ctaButtonStyles from '@/components/CTA/CTA.module.scss';
import { FeatureAnnouncementHero } from '@/components/modules/FeatureAnnouncementHero';
import { LoadMoreButton } from '@/components/modules/LoadMoreButton';
import { MonthGroupSection } from '@/components/modules/MonthGroupSection';
import { OtherImprovementsSection } from '@/components/modules/OtherImprovementsSection';
import { SocialSection } from '@/components/modules/SocialSection';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchProductUpdatesMonthGroup } from './fetchProductUpdatesMonthGroup';
import { mergeMonthGroupIntoList } from './mergeMonthGroupIntoList';
import styles from './FeatureAnnouncementContainer.module.scss';
import { messages } from './messages';
import { useProductUpdatesHashDeepLink } from './useProductUpdatesHashDeepLink';

const NavbarComponent = dynamic(
  () =>
    import(/* webpackChunkName: "navbar_component" */ '@/components/global').then(
      (mod) => mod.Navbar,
    ),
  { ssr: true, loading: () => null },
);

const FooterComponent = dynamic(
  () =>
    import(/* webpackChunkName: "footer_component" */ '@/components/global').then(
      (mod) => mod.Footer,
    ),
  { ssr: true },
);

export interface FeatureAnnouncementContainerProps {
  env: Env;
  pageData: FeatureAnnouncementPageDefinition;
}

export const FeatureAnnouncementContainer: React.FC<FeatureAnnouncementContainerProps> = ({
  pageData,
  env,
}) => {
  const { setPageTheme } = useTheme();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (pageData.theme && pageData.theme !== '') {
      setPageTheme(pageData.theme as Theme);
    }
  }, [pageData.theme, setPageTheme]);

  const [monthGroups, setMonthGroups] = useState<MonthGroup[]>(pageData.monthGroups);
  const [cursor, setCursor] = useState<string | undefined>(pageData.nextCursor);
  const [hasMore, setHasMore] = useState(pageData.hasMore);
  const [loading, setLoading] = useState(false);
  const loadingMoreRef = useRef(false);

  const loadMoreMonth = useCallback(async (): Promise<boolean> => {
    if (!cursor || loadingMoreRef.current) {
      return false;
    }
    loadingMoreRef.current = true;
    setLoading(true);
    try {
      const data = await fetchProductUpdatesMonthGroup(cursor, pageData.locale);
      if (data.monthGroup) {
        setCursor(data.nextCursor);
        setHasMore(data.hasMore);
        setMonthGroups((prev) => mergeMonthGroupIntoList(prev, data.monthGroup!));
        return true;
      }
      if (!data.hasMore && !data.nextCursor) {
        setHasMore(false);
      }
      return false;
    } catch (error) {
      console.error('[FeatureAnnouncementContainer] Load more error:', error);
      return false;
    } finally {
      loadingMoreRef.current = false;
      setLoading(false);
    }
  }, [cursor, pageData.locale]);

  const handleLoadMore = useCallback(async () => {
    await loadMoreMonth();
  }, [loadMoreMonth]);

  useProductUpdatesHashDeepLink({
    monthGroups,
    hasMore,
    locale: pageData.locale,
    loadMoreMonth,
  });

  return (
    <>
      {pageData.includeNavbar && pageData.navigation && (
        <NavbarComponent {...pageData.navigation} />
      )}

      <main id="main-content" className={styles.main}>
        <FeatureAnnouncementHero
          title={pageData.hero.title ?? ''}
          subtitle={pageData.hero.subtitle}
          counterText={pageData.hero.counterText}
          image={pageData.hero.image}
          fallbackMedia={
            pageData.monthGroups[0]?.majorAnnouncements
              .filter((card) => card.media)
              .map((card) => ({
                type: card.media!.type,
                src: card.media!.src,
                alt: card.media!.alt,
              })) ?? []
          }
          cta={pageData.hero.cta}
          breadcrumbs={{
            items: [
              { label: 'Home', link: '/' },
              { label: formatMessage(messages.breadcrumbProductUpdates), isCurrentPage: true },
            ],
            position: 'page-top',
            density: 'default',
            alignment: 'left',
          }}
          isFirstModule={true}
        />

        {monthGroups.map((group) => (
          <React.Fragment key={group.key}>
            <MonthGroupSection
              monthGroup={group}
              heading={
                group.isCurrentMonth
                  ? (pageData.currentMajorAnnouncementsHeading ?? '')
                  : (pageData.pastMajorAnnouncementsHeading ?? '')
              }
            />
            <OtherImprovementsSection
              heading={pageData.minorAnnouncementsHeading ?? ''}
              items={group.minorAnnouncements}
            />
          </React.Fragment>
        ))}

        <LoadMoreButton
          label={formatMessage(messages.loadMore)}
          onClick={handleLoadMore}
          loading={loading}
          visible={hasMore}
        />

        {pageData.secondaryCta && (
          <div className={styles.secondaryCtaWrapper}>
            <a
              href={pageData.secondaryCta.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames(
                ctaButtonStyles['cta-btn'],
                ctaButtonStyles[`cta-btn_type__${pageData.secondaryCta.type.toLowerCase()}`],
              )}
            >
              {pageData.secondaryCta.label}
            </a>
          </div>
        )}

        {pageData.showShareSection && pageData.shareSectionLink && (
          <SocialSection buttonLink={pageData.shareSectionLink} />
        )}
      </main>

      {pageData.includeFooter && (
        <FooterComponent env={env} marketingSectionData={pageData.footer} />
      )}
    </>
  );
};
